import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { isAdmin } from "../shared utils/auth";
import { apiRequest } from "../Timetable/utils/http.js";
import {
  getStaffHours,
  listAllCampuses,
  listOvertimeForReview,
  reviewOvertime,
} from "./api/staffHoursApi";
import { formatHours, isOwnEntry, isReviewable, STATUS_COLOUR, summarise } from "./utils/hours";
import {
  campusChoices,
  columnTotals,
  datesBetween,
  hoursCsv,
  hoursCsvFilename,
  pivotHours,
} from "./utils/hoursPivot";
import { downloadCsv } from "../shared utils/csv";

const STATUS_FILTERS = ["RECORDED", "APPROVED", "REJECTED"];

/**
 * Hours and overtime, for whoever may review them: an admin sees everyone
 * (the paymaster view), a supervisor exactly the people on their supervision
 * list, which Admin → Supervision maintains. The server does the scoping;
 * this screen does not filter by person itself.
 *
 * A supervisor with an empty list sees an empty queue. That is not an error,
 * and the screen says so rather than looking broken.
 */
export default function StaffHoursPage({ selectedDate, selectedCampusId }) {
  const [tab, setTab] = useState(0);

  const [from, setFrom] = useState(() =>
    dayjs(selectedDate || undefined).startOf("week").format("YYYY-MM-DD")
  );
  const [to, setTo] = useState(() =>
    dayjs(selectedDate || undefined).endOf("week").format("YYYY-MM-DD")
  );
  const [status, setStatus] = useState("RECORDED");

  // Campuses are chosen by name here, not id: that is what the hours
  // endpoint takes, and the same campus has a row per season.
  const [campusRows, setCampusRows] = useState([]);
  const [campusesLoading, setCampusesLoading] = useState(true);
  const [campusName, setCampusName] = useState("");
  const campuses = useMemo(() => campusChoices(campusRows), [campusRows]);

  useEffect(() => {
    let cancelled = false;
    listAllCampuses()
      .then((rows) => {
        if (cancelled) return;
        setCampusRows(rows);
        // Open on whichever campus the rest of the app is looking at.
        const current = rows.find((c) => String(c.id) === String(selectedCampusId));
        if (current?.name) setCampusName(current.name);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCampusesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCampusId]);

  const [entries, setEntries] = useState([]);
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Who am I? Only to hide the buttons on my own entries — the server refuses
  // self-review anyway, admins included.
  const myEmail = sessionStorage.getItem("userEmail");
  const [staffByEmail, setStaffByEmail] = useState(() => new Map());
  useEffect(() => {
    let cancelled = false;
    apiRequest("api/staff")
      .then((rows) => {
        if (cancelled) return;
        const map = new Map();
        (Array.isArray(rows) ? rows : []).forEach((s) =>
          map.set(String(s.username || "").toLowerCase(), Number(s.staffid))
        );
        setStaffByEmail(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const [reviewing, setReviewing] = useState(null); // { entry, decision }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(
        await listOvertimeForReview({ from, to, status: status || undefined, campus: campusName || undefined })
      );
    } catch (e) {
      setError(e?.message || "Failed to load the review queue");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, status, campusName]);

  const loadHours = useCallback(async () => {
    if (!campusName) {
      setHours([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setHours(await getStaffHours({ campus: campusName, from, to }));
    } catch (e) {
      setError(e?.message || "Failed to load the hours report");
      setHours([]);
    } finally {
      setLoading(false);
    }
  }, [campusName, from, to]);

  useEffect(() => {
    if (tab === 0) loadQueue();
    else loadHours();
  }, [tab, loadQueue, loadHours]);

  const totals = useMemo(() => summarise(entries), [entries]);

  const submitReview = async () => {
    if (!reviewing) return;
    setSaving(true);
    try {
      await reviewOvertime(reviewing.entry.id, reviewing.decision, note);
      setNotice(
        `${reviewing.entry.staffName}: ${reviewing.decision === "APPROVED" ? "approved" : "rejected"}`
      );
      setReviewing(null);
      setNote("");
      await loadQueue();
    } catch (e) {
      setError(e?.message || "Failed to record the decision");
    } finally {
      setSaving(false);
    }
  };

  // The dates are the columns, so they come from the window rather than from
  // whatever the feed happened to return: a day nobody worked is still a day.
  const dates = useMemo(() => datesBetween(from, to), [from, to]);
  const pivoted = useMemo(() => pivotHours(hours, dates), [hours, dates]);
  const dayTotals = useMemo(() => columnTotals(pivoted, dates), [pivoted, dates]);

  const handleDownload = () => {
    downloadCsv(
      hoursCsvFilename({ campus: campusName, from, to }),
      hoursCsv({ pivoted, dates, campus: campusName, from, to })
    );
  };

  return (
    // .App centres text app-wide; this screen is tables and prose.
    <Box p={2} sx={{ textAlign: "left" }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Typography variant="h4">Hours &amp; Overtime</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {isAdmin()
              ? "Everyone — the paymaster view."
              : "The people you supervise."}
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="From"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl size="small" sx={{ minWidth: 190 }} disabled={campusesLoading}>
            <InputLabel>Campus</InputLabel>
            <Select
              label="Campus"
              value={campuses.some((c) => c.name === campusName) ? campusName : ""}
              onChange={(e) => setCampusName(e.target.value)}
            >
              <MenuItem value="">
                <em>{tab === 0 ? "All campuses" : "Select campus…"}</em>
              </MenuItem>
              {campuses.map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                  {c.active ? "" : " — past season"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {tab === 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                {STATUS_FILTERS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s.charAt(0) + s.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button variant="outlined" onClick={() => (tab === 0 ? loadQueue() : loadHours())} disabled={loading}>
            Refresh
          </Button>

          {loading && <CircularProgress size={20} sx={{ alignSelf: "center" }} />}
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Overtime review" />
          <Tab label="Hours report" />
        </Tabs>

        {tab === 0 && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                color={totals.pending ? "warning" : "default"}
                label={`${totals.pending} awaiting review${
                  totals.pending ? ` • ${formatHours(totals.pendingMinutes)}` : ""
                }`}
              />
              {totals.approvedMinutes > 0 && (
                <Chip size="small" color="success" label={`Approved ${formatHours(totals.approvedMinutes)}`} />
              )}
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Staff</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Campus</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Decision</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e) => {
                    const mine = isOwnEntry(e, myEmail, staffByEmail);
                    return (
                      <TableRow key={e.id} hover>
                        <TableCell>{e.staffName}</TableCell>
                        <TableCell>{e.workDate}</TableCell>
                        <TableCell>{e.campus || "—"}</TableCell>
                        <TableCell align="right">{formatHours(e.minutes)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>{e.reason || "—"}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={e.status} color={STATUS_COLOUR[e.status] || "default"} />
                            {e.reviewedByName && (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                by {e.reviewedByName}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          {!isReviewable(e) ? (
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              {e.reviewNote || "Final"}
                            </Typography>
                          ) : mine ? (
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              Your own — someone else reviews it
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => {
                                  setNote("");
                                  setReviewing({ entry: e, decision: "APPROVED" });
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                  setNote("");
                                  setReviewing({ entry: e, decision: "REJECTED" });
                                }}
                              >
                                Reject
                              </Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {entries.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3, opacity: 0.75 }}>
                        {isAdmin()
                          ? "No overtime in this window."
                          : "Nothing to review. Either nobody you supervise has recorded overtime in this window, or nobody is on your supervision list yet — an admin sets that up."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownload}
                disabled={pivoted.length === 0}
              >
                Download CSV
              </Button>
              {pivoted.length > 0 && (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  {dates.length} day{dates.length === 1 ? "" : "s"} · total{" "}
                  {formatHours(dayTotals.total)}
                </Typography>
              )}
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      Staff
                    </TableCell>
                    {dates.map((d) => (
                      <TableCell key={d} align="right">
                        {d.slice(8, 10)}/{d.slice(5, 7)}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pivoted.map((row) => (
                    <TableRow key={`${row.staffId}-${row.measure}`} hover>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        {row.label}
                      </TableCell>
                      {row.values.map((v, i) => (
                        <TableCell key={dates[i]} align="right">
                          {formatHours(v)}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatHours(row.total)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {pivoted.length > 0 && (
                    <TableRow>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          bgcolor: "background.paper",
                          fontWeight: 700,
                        }}
                      >
                        All staff — total
                      </TableCell>
                      {dayTotals.perDate.map((v, i) => (
                        <TableCell key={dates[i]} align="right" sx={{ fontWeight: 700 }}>
                          {formatHours(v)}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatHours(dayTotals.total)}
                      </TableCell>
                    </TableRow>
                  )}

                  {pivoted.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={dates.length + 2} align="center" sx={{ py: 3, opacity: 0.75 }}>
                        {campusName
                          ? "No hours for this campus and window."
                          : "Pick a campus to show the hours report."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

      </Stack>

      <Dialog open={Boolean(reviewing)} onClose={() => !saving && setReviewing(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {reviewing?.decision === "APPROVED" ? "Approve overtime" : "Reject overtime"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              {reviewing?.entry?.staffName} • {reviewing?.entry?.workDate} •{" "}
              {formatHours(reviewing?.entry?.minutes)}
            </Typography>
            {reviewing?.entry?.reason && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {reviewing.entry.reason}
              </Typography>
            )}
            <TextField
              size="small"
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
            />
            <Alert severity="info">The decision is final — it cannot be changed afterwards.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewing?.decision === "APPROVED" ? "success" : "error"}
            onClick={submitReview}
            disabled={saving}
          >
            {saving ? "Saving…" : reviewing?.decision === "APPROVED" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
